import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { getOwner } from '@ember/application';
import { stopMotion, playMotion } from '../lib/nv-motion.js';

/**
 * MAP (in-file navigation graph — keep in sync with the code):
 *   role:      modifier — «UI nuova» one-shot animation when a shown value CHANGES (M19 item 22: the paid chip and
 *              the amount of an invoice, a status chip, a tab count, the tab pane). Usage:
 *              `<span {{nv-flash-on-change @pill.label kind='pop'}}>`.
 *   behaviour: the first value seen is the baseline (no animation on the first render, nor when the element is
 *              re-created). A later different value plays lib/nv-motion `kind` once. Not animated: a value that
 *              appears or clears (blank ↔ value: a load, not a change), a change that comes with another record
 *              (the `scope` arg, else the closest [data-nv-scope] — NvDetailFrame sets the record id — plus the
 *              router path).
 *   args:      positional[0] value (compared as a string); named `kind` ('pop' default | 'rise' | 'fade'),
 *              `hold` (truthy while a save is in flight: the change is held until the server answered, so a
 *              refused save that rolls the value back never animates — the codebase is non-optimistic),
 *              `scope` (optional key that resets the baseline when it changes, e.g. the record id).
 *   uses:      ember-modifier (class), lib/nv-motion (classes + timer), service:router (currentURL, optional)
 *   css:       app/styles/nuova/_motion.scss (.nv-flash--<kind>)
 *   gotchas:   inside an {{#each}} whose items are rebuilt on every compute (getters returning new POJOs) the row is
 *              re-created, so the modifier only sees a first value: key the each (`key='@index'` / a stable field).
 *              Classic UI: no class is ever added (lib/nv-motion isNuovaDom).
 *   tests:     tests/integration/modifiers/nv-flash-on-change-test.js
 */
const keyOf = value => value === undefined || value === null || value === '' || value === false ? null : String(value);
class NvFlashOnChangeModifier extends Modifier {
  constructor(owner, args) {
    super(owner, args);
    this.element = null;
    this.started = false;
    this.baseline = null;
    this.scope = null;
    registerDestructor(this, () => {
      if (this.element) stopMotion(this.element);
    });
  }

  /** The page the value belongs to: another path means another record, not a change of this one. */
  get routeScope() {
    let url = null;
    try {
      url = getOwner(this)?.lookup('service:router')?.currentURL;
    } catch {
      url = null; // no router (rendering test, engine without routes): no page scope
    }
    return typeof url === 'string' ? url.split('?')[0] : null;
  }

  /** Without a `scope` arg: the closest record frame ([data-nv-scope], NvDetailFrame) on this page. */
  pageScope(element) {
    const frame = element.closest?.('[data-nv-scope]')?.getAttribute('data-nv-scope') ?? '';
    return `${frame}|${this.routeScope ?? ''}`;
  }
  modify(element, [value], {
    kind,
    hold,
    scope
  }) {
    if (this.element !== element) {
      if (this.element) stopMotion(this.element);
      this.element = element;
      this.started = false;
    }
    const next = keyOf(value);
    const scopeNow = scope === undefined ? this.pageScope(element) : keyOf(scope);
    if (!this.started || scopeNow !== this.scope) {
      this.started = true;
      this.scope = scopeNow;
      this.baseline = next;
      return;
    }
    // a save in flight: keep the last confirmed value, decide when the server has answered
    if (hold) return;
    if (next === this.baseline) return;
    const previous = this.baseline;
    this.baseline = next;
    if (previous === null || next === null) return;
    playMotion(element, kind);
  }
}

export { NvFlashOnChangeModifier as default };
//# sourceMappingURL=nv-flash-on-change.js.map
